// ============================================================================
// CONFIG SETTING FIELD
// ============================================================================
// Renders a single config setting with the appropriate control by dataType.
// Ported from crs-web/src/components/modules/config-module/ConfigSettingField.tsx.

import React, { useMemo, useCallback } from 'react';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect/SyutSelectUnified';
import ConfigListEditor from './ConfigListEditor';
import type {
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types';
import type { ScopeType } from 'hooks/api/useConfigSettings';
import { countryService } from 'api/services/countryService';
import { currencyService } from 'api/services/currencyService';
import { timezoneService } from 'api/services/timezoneService';

// ----------------------------------------------------------------------------
// BMS lookup loaders — used when definition.metaData includes
//   { "lookup": "country" | "currency" | "timezone" }
// The persisted value is the *code* (e.g. "IN", "INR", "Asia/Kolkata"), so
// SelectOption.value MUST be the code (NOT the UUID id).
// ----------------------------------------------------------------------------
type LookupKind = 'country' | 'currency' | 'timezone';

const lookupLoaders: Record<LookupKind, (input: string) => Promise<SelectOption[]>> = {
  country: async (input) => {
    const res = await countryService.getAll();
    const all = (res.data ?? []).map((c: any) => ({
      value: c.countryCode,
      label: `${c.countryCode} — ${c.countryName}`,
    }));
    if (!input) return all;
    const q = input.toLowerCase();
    return all.filter((o) => o.label.toLowerCase().includes(q));
  },
  currency: async (input) => {
    const res = await currencyService.getAll();
    const all = (res.data ?? []).map((c: any) => {
      const symbol = c.currencySymbol ? ` ${c.currencySymbol}` : '';
      return {
        value: c.currencyCode,
        label: `${c.currencyCode}${symbol} — ${c.currencyName}`,
      };
    });
    if (!input) return all;
    const q = input.toLowerCase();
    return all.filter((o) => o.label.toLowerCase().includes(q));
  },
  timezone: async (input) => {
    const res = await timezoneService.getAll();
    const all = (res.data ?? []).map((t: any) => ({
      value: t.tzCode,
      label: t.utcOffsetDisplay ? `${t.tzCode} (${t.utcOffsetDisplay})` : t.tzCode,
    }));
    if (!input) return all;
    const q = input.toLowerCase();
    return all.filter((o) => o.label.toLowerCase().includes(q));
  },
};

interface ConfigSettingFieldProps {
  definition: ConfigDefinition;
  effectiveValue: ConfigEffectiveValue;
  editedValue?: ConfigValueUpdate;
  onValueChange: (configKey: string, value?: string, listValues?: ConfigListItem[]) => void;
  onResetValue: (configKey: string) => void;
  scopeType: ScopeType;
  disabled?: boolean;
  disabledReason?: string;
}

const ConfigSettingField: React.FC<ConfigSettingFieldProps> = ({
  definition,
  effectiveValue,
  editedValue,
  onValueChange,
  onResetValue,
  scopeType,
  disabled: gatedDisabled,
  disabledReason,
}) => {
  // System-only / office-only flags
  const isSystemOnly = definition.maxScopeLevel === 0;
  const isOfficeOnly = definition.maxScopeLevel <= 1;
  const isReadOnly =
    (isSystemOnly && scopeType !== 'SYSTEM') ||
    (isOfficeOnly && scopeType === 'EMPLOYEE') ||
    !!gatedDisabled;

  // Determine badge type
  const badge = useMemo(() => {
    if (scopeType === 'SYSTEM') return null;

    if (isSystemOnly) {
      return { className: 'config-badge-system-only', label: 'System Only' };
    }
    if (scopeType === 'EMPLOYEE' && isOfficeOnly) {
      return { className: 'config-badge-system-only', label: 'Office Max' };
    }

    const isOverridden =
      effectiveValue.resolvedFromScopeType === scopeType ||
      (editedValue && editedValue.value !== undefined);
    if (isOverridden) {
      return { className: 'config-badge-override', label: 'Override' };
    }
    return { className: 'config-badge-inherited', label: 'Inherited' };
  }, [isSystemOnly, isOfficeOnly, scopeType, effectiveValue, editedValue]);

  // Current displayed value
  const currentValue = useMemo(() => {
    if (editedValue?.value !== undefined) return editedValue.value ?? '';
    return effectiveValue.effectiveValue ?? definition.defaultValue ?? '';
  }, [editedValue, effectiveValue, definition]);

  // Current list values
  const currentListValues = useMemo<ConfigListItem[]>(() => {
    if (editedValue?.listValues) return editedValue.listValues;
    return effectiveValue.listValues ?? [];
  }, [editedValue, effectiveValue]);

  const originalListValues = useMemo(
    () => new Set((effectiveValue.listValues ?? []).map((i) => i.value)),
    [effectiveValue]
  );

  // Whether this field has an override at the current scope that can be reset
  const canReset =
    scopeType !== 'SYSTEM' &&
    !isReadOnly &&
    (effectiveValue.resolvedFromScopeType === scopeType ||
      editedValue !== undefined);

  const handleChange = useCallback(
    (value: string) => {
      onValueChange(definition.configKey, value);
    },
    [definition.configKey, onValueChange]
  );

  const handleListChange = useCallback(
    (listValues: ConfigListItem[]) => {
      onValueChange(definition.configKey, undefined, listValues);
    },
    [definition.configKey, onValueChange]
  );

  const handleReset = useCallback(() => {
    onResetValue(definition.configKey);
  }, [definition.configKey, onResetValue]);

  // Parse meta_data once per definition. Returns either:
  //   - { kind: 'lookup', source: 'country' | 'currency' | 'timezone' }  → async BMS dropdown
  //   - { kind: 'allowed', options: [...] }                              → static dropdown
  //   - null                                                             → no dropdown override
  const metaConfig = useMemo<
    | { kind: 'lookup'; source: LookupKind }
    | { kind: 'allowed'; options: SelectOption[] }
    | null
  >(() => {
    if (!definition.metaData) return null;
    try {
      const parsed = JSON.parse(definition.metaData);
      if (typeof parsed?.lookup === 'string') {
        const src = parsed.lookup as LookupKind;
        if (src in lookupLoaders) return { kind: 'lookup', source: src };
      }
      const arr = parsed?.allowed_values;
      if (Array.isArray(arr) && arr.length > 0) {
        const opts = arr
          .filter((v: unknown) => typeof v === 'string' && v.length > 0)
          .map((v: string) => ({ value: v, label: v.replace(/_/g, ' ') }));
        return { kind: 'allowed', options: opts };
      }
    } catch {
      /* fall through */
    }
    return null;
  }, [definition.metaData]);

  // Render the appropriate control
  const renderControl = () => {
    // BMS lookup (country/currency/timezone) → async dropdown
    if (metaConfig?.kind === 'lookup') {
      const loader = lookupLoaders[metaConfig.source];
      // Seed option ensures the selected code shows a label even before the
      // async list loads (e.g., currentValue = "INR" → "INR — Indian Rupee" once loaded;
      // until then the raw code is shown).
      const seed: SelectOption[] = currentValue
        ? [{ value: currentValue, label: currentValue }]
        : [];
      return (
        <div style={{ minWidth: '260px' }}>
          <SyutSelect
            isAsync
            loadOptions={loader}
            defaultOptions={true}
            cacheOptions={false}
            seedOptions={seed}
            value={currentValue || undefined}
            onChange={(value: string | string[] | null) =>
              handleChange(typeof value === 'string' ? value : '')
            }
            isDisabled={isReadOnly}
            isClearable={false}
            size="sm"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPlacement="auto"
          />
        </div>
      );
    }

    // String enum via metaData.allowed_values → static dropdown
    if (metaConfig?.kind === 'allowed') {
      return (
        <div style={{ minWidth: '200px' }}>
          <SyutSelect
            value={currentValue}
            onChange={(value: string | string[] | null) =>
              handleChange(typeof value === 'string' ? value : '')
            }
            options={metaConfig.options}
            isDisabled={isReadOnly}
            size="sm"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPlacement="auto"
          />
        </div>
      );
    }

    switch (definition.dataType) {
      case 'Boolean':
        return (
          <label className="config-toggle-switch">
            <input
              type="checkbox"
              checked={currentValue === 'true' || currentValue === 'True' || currentValue === '1'}
              onChange={(e) => handleChange(e.target.checked ? 'true' : 'false')}
              disabled={isReadOnly}
            />
            <span className="config-toggle-slider" />
          </label>
        );

      case 'Integer':
        return (
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseInt(definition.minValue, 10) : undefined}
            max={definition.maxValue ? parseInt(definition.maxValue, 10) : undefined}
            step={1}
            disabled={isReadOnly}
          />
        );

      case 'Decimal':
        return (
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseFloat(definition.minValue) : undefined}
            max={definition.maxValue ? parseFloat(definition.maxValue) : undefined}
            step={0.01}
            disabled={isReadOnly}
          />
        );

      case 'List':
        return (
          <ConfigListEditor
            values={currentListValues}
            onChange={handleListChange}
            originalValues={originalListValues}
            unitLabel={definition.unitLabel ?? undefined}
            isReadOnly={isReadOnly}
          />
        );

      default:
        // For date-formatted strings, render a date input
        if (definition.metaData) {
          try {
            const parsed = JSON.parse(definition.metaData);
            if (parsed?.format === 'date') {
              return (
                <input
                  type="date"
                  value={currentValue}
                  onChange={(e) => handleChange(e.target.value)}
                  disabled={isReadOnly}
                />
              );
            }
          } catch {
            /* ignore */
          }
        }
        return (
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            disabled={isReadOnly}
          />
        );
    }
  };

  return (
    <div
      className={`config-setting-row${
        definition.dataType === 'List' ? ' config-setting-row--list' : ''
      }`}
      style={gatedDisabled ? { opacity: 0.55 } : undefined}
    >
      <div className="config-setting-info">
        <div className="config-setting-label">
          <span>{definition.displayName}</span>
          {definition.unitLabel && (
            <span className="config-setting-unit">({definition.unitLabel})</span>
          )}
          {badge && (
            <span className={`config-badge ${badge.className}`}>{badge.label}</span>
          )}
        </div>
        {definition.description && (
          <div className="config-setting-description">{definition.description}</div>
        )}
        {gatedDisabled && disabledReason && (
          <div
            className="config-setting-description"
            style={{ fontStyle: 'italic', marginTop: '0.25rem' }}
          >
            {disabledReason}
          </div>
        )}
      </div>
      <div className="config-setting-control">
        {renderControl()}
        {canReset && !gatedDisabled && (
          <button
            type="button"
            className="config-reset-btn"
            onClick={handleReset}
            title="Reset to system default"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default ConfigSettingField;
